import { CollisionPriority } from '@dnd-kit/abstract';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider, DragEndEvent, useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import classNames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { EyeIcon, EyeSlashIcon } from 'components/userPreferences/form/tabs/icons';
import type { IntlKey } from 'locales';
import { FC, useCallback, useState } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { TabForm } from './TabForm';

const hideable = ['DailyBlotterTab', 'ActorsTab'];

interface TabProps {
  onClose: () => void;
  hiddenSpmTabs: readonly string[];
  visibleSpmTabs: readonly string[];
  onSpmTabsUpdated(visibleTabs: string[], hiddenTabs: string[]): void;
  onReset(): void;
}

interface TabsListProps {
  tabs: { id: string }[];
  id: string;
  className?: string;
  group: DroppableColumns;
}

function getUserPreferenceFormTabOrderTranslationKey(id: string): IntlKey {
  switch (id) {
    case 'hidden':
      return 'userPreferencesForm.tabsOrder.hidden';
    case 'visible':
      return 'userPreferencesForm.tabsOrder.visible';
    default:
      throw Error(`Unknown tab id : ${id}`);
  }
}

const TabsList: FC<TabsListProps> = ({ tabs, id, className, group }) => {
  const { ref } = useDroppable({
    id: group,
    type: 'spm-node',
    accept: 'spm-node',
    collisionPriority: CollisionPriority.Low,
  });

  return (
    <div id={`${id}-spm`} className={classNames('card card-bordered flex-basis-100', className)}>
      <div className="card-header">
        <h3>
          <FormattedMessage id={getUserPreferenceFormTabOrderTranslationKey(id)} />
        </h3>
      </div>
      <div className="card-body" ref={ref}>
        {tabs.map((t, idx) => (
          <DraggableListItem label={t.id} key={t.id} draggableGroup={group} index={idx} />
        ))}
      </div>
    </div>
  );
};

type DraggableListItemProps = {
  label: string;
  index: number;
  draggableGroup: string;
};

const DraggableListItem = ({ label, index, draggableGroup }: DraggableListItemProps) => {
  const { ref } = useSortable({
    id: label,
    index,
    type: 'spm-node',
    group: draggableGroup,

    data: {
      container: draggableGroup,
    },
  });

  return (
    <div className="block my-1 p-3 bg-primary-alt border" ref={ref}>
      <h6 className="d-flex justify-content-between align-items-center mb-0">
        <FormattedMessage id={`form.${label}` as any} />
        {hideable.includes(label) && draggableGroup === 'visibleSpmTabs' && <EyeIcon />}
        {hideable.includes(label) && draggableGroup === 'hiddenSpmTabs' && <EyeSlashIcon />}
      </h6>
    </div>
  );
};

type DroppableColumns = 'visibleSpmTabs' | 'hiddenSpmTabs';
type SortTabState = {
  [key in DroppableColumns]: { id: string }[];
};

const mapToState = (visibleSpmTabs: readonly string[], hiddenSpmTabs: readonly string[]): SortTabState => ({
  ['visibleSpmTabs']: visibleSpmTabs.map(f => ({ id: f })),
  ['hiddenSpmTabs']: hiddenSpmTabs.map(f => ({ id: f })),
});

export const SortTabForm: FC<TabProps> = ({ onClose, onSpmTabsUpdated, onReset, hiddenSpmTabs, visibleSpmTabs }) => {
  const [items, setDnDItems] = useState<SortTabState>(mapToState(visibleSpmTabs, hiddenSpmTabs));

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.operation.canceled) {
        return;
      }

      const draggedId = event.operation.source?.id as string;
      const targetGroup = event.operation.target?.id;
      // When we use the sortable hook, the target is a list item, not the droppable column behind,
      // so we check in the data of the list item
      const containerInlistItemData = event.operation.target?.data.container;
      if (
        (targetGroup === 'hiddenSpmTabs' || containerInlistItemData === 'hiddenSpmTabs') &&
        !hideable.includes(draggedId)
      ) {
        return;
      }

      setDnDItems(prev => {
        const newItems = move(prev, event);
        onSpmTabsUpdated(
          newItems.visibleSpmTabs.map(t => t.id),
          newItems.hiddenSpmTabs.map(t => t.id),
        );
        logAnalytics.trackEvent('User preferences', 'Change tab order/visibility');
        return newItems;
      });
    },
    [onSpmTabsUpdated],
  );

  return (
    <TabForm labelKey="userPreferencesForm.tabsOrder.label" onClose={onClose} className="pb-4">
      <button type="button" className="btn btn-default btn-xl mb-3" onClick={onReset}>
        <FormattedMessage id="userPreferencesForm.tabsOrder.reset" />
      </button>

      <DragDropProvider
        onDragOver={event => {
          const draggedId = event.operation.source?.id as string;
          const targetGroup = event.operation.target?.id;

          // When we use the sortable hook, the target is a list item, not the droppable column behind,
          // so we check in the data of the list item
          const containerInlistItemData = event.operation.target?.data.container;
          if (
            (targetGroup === 'hiddenSpmTabs' || containerInlistItemData === 'hiddenSpmTabs') &&
            !hideable.includes(draggedId)
          ) {
            event.preventDefault();
            return;
          }

          setDnDItems(prev => move(prev, event));
        }}
        onDragEnd={onDragEnd}
      >
        <div className="d-flex">
          <TabsList tabs={items['visibleSpmTabs']} id="visible" group={'visibleSpmTabs'} />
          <TabsList tabs={items['hiddenSpmTabs']} id="hidden" group={'hiddenSpmTabs'} className="ms-5" />
        </div>
      </DragDropProvider>
    </TabForm>
  );
};

SortTabForm.displayName = 'SortTabForm:';
